import classNames from 'classnames'
import { getDefaultRegistry } from '@rjsf/core'
import { canExpand, getTemplate, getUiOptions } from '@rjsf/utils'
import type { ArrayFieldTemplateProps, FieldTemplateProps, IconButtonProps, ObjectFieldTemplateProps, RJSFSchema, TemplatesType } from '@rjsf/utils'
import { AddIconButton, RemoveIconButton } from './IconButtons'
import { humanizeVariableName } from '../../variableLabel'

const isLocked = (props: { disabled?: boolean; readonly?: boolean }): boolean => Boolean(props.disabled || props.readonly)

const rootIdFromFormContext = (formContext: unknown): string => {
  if (formContext && typeof formContext === 'object' && 'rootId' in formContext) {
    const value = (formContext as { rootId?: unknown }).rootId
    if (typeof value === 'string') {
      return value
    }
  }
  return 'root'
}

const { ButtonTemplates: defaultButtonTemplates } = getDefaultRegistry().templates

export const parameterTemplates: Partial<TemplatesType> = {
  FieldTemplate: (props: FieldTemplateProps) => {
    const {
      id,
      classNames: baseClassName,
      style,
      label,
      help,
      description,
      errors,
      children,
      hidden,
      required,
      displayLabel,
      registry,
      uiSchema,
      schema,
    } = props
    const fieldLabel = typeof schema.title === 'string' && schema.title ? label : humanizeVariableName(label)

    if (hidden) {
      return null
    }

    const WrapIfAdditionalTemplate = getTemplate('WrapIfAdditionalTemplate', registry, getUiOptions(uiSchema))

    return (
      <WrapIfAdditionalTemplate {...props}>
        <div className={classNames(baseClassName, 'mb-3')} style={style}>
          {displayLabel && fieldLabel ? (
            <label htmlFor={id} className='paragraph-medium bold d-block mb-1'>
              {fieldLabel}
              {required ? <span className='zocks-text-danger'> *</span> : null}
            </label>
          ) : null}
          {displayLabel && description ? <div className='paragraph-small text-muted mb-1'>{description}</div> : null}
          {children}
          {errors ? <div className='paragraph-small zocks-text-danger mt-1'>{errors}</div> : null}
          {help}
        </div>
      </WrapIfAdditionalTemplate>
    )
  },
  ObjectFieldTemplate: (props: ObjectFieldTemplateProps) => {
    const { title, description, properties, idSchema, schema, uiSchema, formData, onAddClick, disabled, readonly, registry } = props
    const isRoot = idSchema.$id === rootIdFromFormContext(registry.formContext)
    const heading = typeof schema.title === 'string' && schema.title ? schema.title : title ? humanizeVariableName(title) : undefined
    const headingDescription = description || schema.description

    return (
      <div className='d-flex flex-column'>
        {!isRoot && heading ? <div className='paragraph-medium bold mb-1'>{heading}</div> : null}
        {!isRoot && headingDescription ? <div className='paragraph-small text-muted mb-2'>{headingDescription}</div> : null}
        {properties.map((property) => (
          <div key={property.name}>{property.content}</div>
        ))}
        {canExpand(schema, uiSchema, formData) ? (
          <AddIconButton label='Add property' className='align-self-start mt-2' disabled={isLocked({ disabled, readonly })} onClick={onAddClick(schema)} />
        ) : null}
      </div>
    )
  },
  ArrayFieldTemplate: (props: ArrayFieldTemplateProps) => {
    const { items, canAdd, onAddClick, disabled, readonly, schema, title, idSchema, registry } = props
    const itemsSchema = Array.isArray(schema.items) ? undefined : (schema.items as RJSFSchema | undefined)
    const itemTitle = itemsSchema?.title || 'item'
    const isRoot = idSchema.$id === rootIdFromFormContext(registry.formContext)
    const heading = typeof schema.title === 'string' && schema.title ? schema.title : title ? humanizeVariableName(title) : undefined
    const headingDescription = schema.description

    return (
      <div className='d-flex flex-column'>
        {!isRoot && heading ? <div className='paragraph-medium bold mb-1'>{heading}</div> : null}
        {!isRoot && headingDescription ? <div className='paragraph-small text-muted mb-2'>{headingDescription}</div> : null}
        {items.map((item, index) => (
          <div key={item.key} className={classNames('d-flex align-items-start gap-2 py-2', { 'border-top': index > 0 })}>
            <div className='flex-grow-1'>{item.children}</div>
            {item.hasRemove ? <RemoveIconButton disabled={isLocked(item)} onClick={item.onDropIndexClick(item.index)} /> : null}
          </div>
        ))}
        {canAdd ? (
          <AddIconButton label={`Add ${itemTitle}`} className='align-self-start mt-2' disabled={isLocked({ disabled, readonly })} onClick={onAddClick} />
        ) : null}
      </div>
    )
  },
  ButtonTemplates: {
    ...defaultButtonTemplates,
    AddButton: ({ className, style, disabled, onClick }: IconButtonProps) => (
      <AddIconButton label='Add item' onClick={onClick} disabled={disabled} className={className} style={style} />
    ),
    RemoveButton: ({ className, style, disabled, onClick }: IconButtonProps) => (
      <RemoveIconButton onClick={onClick} disabled={disabled} className={className} style={style} />
    ),
    MoveUpButton: () => null,
    MoveDownButton: () => null,
  },
}
